import type { LucideIcon } from "lucide-react";
import {
  Briefcase,
  Building2,
  Calculator,
  Car,
  CircleHelp,
  Leaf,
  MapPinned,
  Newspaper,
  Route,
  Search,
  ShieldCheck,
  Sparkles,
  Ticket,
  UserRound,
  Users,
  Waypoints,
} from "lucide-react";
import { ROUTE_PATHS } from "@/routes/routePaths";

export type FooterNavLink = {
  name: string;
  href: string;
  requiresAuth?: boolean;
  external?: boolean;
  /** Optional icon for header mega-menu items */
  Icon?: LucideIcon;
};

export type FooterNavSection = {
  id: "company" | "guests" | "hosts" | "support" | "legal" | "commute";
  title: string;
  links: FooterNavLink[];
  /** Optional section icon for header trigger */
  Icon?: LucideIcon;
};

export const FOOTER_EXTERNAL = {
  WHATSAPP_SUPPORT: "https://wa.me/917680938741",
  DURI_GREEN: "https://green.duriapp.com/",
} as const;

export const TERMS_PATH = "/TermsAndConditions";

/** Sections shown as header mega/dropdown menus. */
export const HEADER_NAV_SECTION_IDS = ["company", "guests", "hosts"] as const;

type TranslateFn = (key: string, section?: string) => string;

/**
 * Single source of truth for footer (and header dropdown) link sections.
 */
export function buildFooterNavSections(t: TranslateFn): FooterNavSection[] {
  const tf = (key: string) => t(key, "footer");

  return [
    {
      id: "company",
      title: tf("sectionCompany"),
      Icon: Building2,
      links: [
        { name: tf("companyLinks.aboutUs"), href: ROUTE_PATHS.ABOUT_DURI, Icon: Building2 },
        { name: tf("companyLinks.news"), href: ROUTE_PATHS.NEWS, Icon: Newspaper },
        {
          name: t("benefits", "header"),
          href: ROUTE_PATHS.RIDESHARING_BENEFITS,
          Icon: Sparkles,
        },
        {
          name: tf("companyLinks.howItWorks"),
          href: ROUTE_PATHS.HOW_DURI_WORKS,
          Icon: Waypoints,
        },
        { name: tf("companyLinks.safety"), href: ROUTE_PATHS.SAFETY, Icon: ShieldCheck },
        {
          name: tf("companyLinks.duriGreenMobility"),
          href: FOOTER_EXTERNAL.DURI_GREEN,
          external: true,
          Icon: Leaf,
        },
      ],
    },
    {
      id: "guests",
      title: tf("sectionGuests"),
      Icon: UserRound,
      links: [
        {
          name: tf("guestLinks.howToBook"),
          href: ROUTE_PATHS.HOW_DURI_WORKS,
          Icon: CircleHelp,
        },
        { name: tf("guestLinks.findARide"), href: ROUTE_PATHS.FIND_RIDE, Icon: Search },
        { name: tf("guestLinks.availableRides"), href: ROUTE_PATHS.FIND_RIDES, Icon: Car },
        {
          name: tf("guestLinks.searchByRideId"),
          href: ROUTE_PATHS.RIDE_INFO,
          Icon: Ticket,
        },
        {
          name: tf("guestLinks.searchByRider"),
          href: ROUTE_PATHS.TRACK_RIDE,
          Icon: MapPinned,
        },
        {
          name: tf("guestLinks.howToDailyCommute"),
          href: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
          Icon: Briefcase,
        },
        {
          name: tf("guestLinks.commuteCalculator"),
          href: `${ROUTE_PATHS.COMMUTE_CALCULATOR}?role=guest`,
          Icon: Calculator,
        },
      ],
    },
    {
      id: "hosts",
      title: tf("sectionHosts"),
      Icon: Users,
      links: [
        { name: tf("hostLinks.howToHost"), href: ROUTE_PATHS.OFFER_A_RIDE, Icon: Car },
        { name: tf("hostLinks.rideRequests"), href: ROUTE_PATHS.RIDE_REQUESTS, Icon: Users },
        { name: tf("hostLinks.lookUpARide"), href: ROUTE_PATHS.TRACK_RIDE, Icon: Search },
        {
          name: tf("hostLinks.howToDailyCommute"),
          href: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
          Icon: Briefcase,
        },
        {
          name: tf("hostLinks.popularRoutes"),
          href: ROUTE_PATHS.POPULAR_ROUTES,
          Icon: Route,
        },
        {
          name: tf("hostLinks.commuteCalculator"),
          href: `${ROUTE_PATHS.COMMUTE_CALCULATOR}?role=host`,
          Icon: Calculator,
        },
      ],
    },
    {
      id: "support",
      title: tf("sectionSupport"),
      links: [
        { name: t("faqs", "header"), href: ROUTE_PATHS.FAQS },
        {
          name: tf("supportLinks.reportIssue"),
          href: "/account/ReportAnIssue",
          requiresAuth: true,
        },
        {
          name: tf("supportLinks.myIssues"),
          href: "/account/MyIssues",
          requiresAuth: true,
        },
        { name: tf("supportLinks.getHelp"), href: ROUTE_PATHS.HELP_CENTER },
        {
          name: tf("supportLinks.whatsappSupport"),
          href: FOOTER_EXTERNAL.WHATSAPP_SUPPORT,
          external: true,
        },
        {
          name: tf("supportLinks.corporateCarpool"),
          href: ROUTE_PATHS.CORPORATE_CARPOOL,
        },
      ],
    },
    {
      id: "legal",
      title: tf("sectionLegal"),
      links: [
        { name: tf("legalLinks.termsOfService"), href: TERMS_PATH },
        { name: tf("legalLinks.privacyPolicy"), href: "/privacypolicy" },
        { name: tf("legalLinks.refundPolicy"), href: "/CancellationPolicy" },
        {
          name: tf("legalLinks.deleteAccount"),
          href: "/delete-account",
          requiresAuth: true,
        },
        { name: tf("legalLinks.tripInsurance"), href: TERMS_PATH },
        { name: tf("legalLinks.paymentTerms"), href: TERMS_PATH },
      ],
    },
  ];
}

export function getHeaderCommuteSection(t: TranslateFn): FooterNavSection {
  return {
    id: "commute",
    title: t("commuteCarpool", "header"),
    Icon: Briefcase,
    links: [
      {
        name: t("corporateCarpool", "header"),
        href: ROUTE_PATHS.CORPORATE_CARPOOL,
        Icon: Building2,
      },
      {
        name: t("dailyCommutes", "header"),
        href: ROUTE_PATHS.DAILY_COMMUTE_CARPOOL,
        Icon: Briefcase,
      },
    ],
  };
}

export function getHeaderNavSections(t: TranslateFn): FooterNavSection[] {
  const ids = new Set<string>(HEADER_NAV_SECTION_IDS);
  const primary = buildFooterNavSections(t).filter((section) => ids.has(section.id));
  return [...primary, getHeaderCommuteSection(t)];
}
